'use client';

import { useTranslations } from 'next-intl';
import {
  Loading03Icon,
  Delete02Icon,
  PlayIcon,
  StopIcon,
} from '@terra/ui-icons';
import { ExecutionNeoIcon } from '@terra/ui-design-system';
import { ListPageBulkActionButton } from '../../common/ListPageBulkActionButton';
import { ListPageSelectionBar } from '../../common/ListPageSelectionBar';

type ExecutionsBulkActionsBarProps = {
  selectedCount: number;
  resumableCount: number | null;
  rerunableCount: number | null;
  abortableCount: number | null;
  isBulkResuming: boolean;
  isBulkRerunning: boolean;
  isBulkAborting: boolean;
  isBulkDeleting: boolean;
  isBusy: boolean;
  mutationsDisabledTooltip: string | null;
  onClearSelection: () => void;
  onResume: () => void;
  onRerun: () => void;
  onAbort: () => void;
  onDelete: () => void;
};

const SpinningIcon = () => (
  <Loading03Icon size={12} style={{ animation: 'spin 1s linear infinite' }} />
);

export function ExecutionsBulkActionsBar({
  selectedCount,
  resumableCount,
  rerunableCount,
  abortableCount,
  isBulkResuming,
  isBulkRerunning,
  isBulkAborting,
  isBulkDeleting,
  isBusy,
  mutationsDisabledTooltip,
  onClearSelection,
  onResume,
  onRerun,
  onAbort,
  onDelete,
}: ExecutionsBulkActionsBarProps) {
  const t = useTranslations('proxy.executions.bulkActions');
  const tCommon = useTranslations('designSystem.common');
  const isLocked = isBusy || mutationsDisabledTooltip !== null;
  const resumeLabel =
    resumableCount === null
      ? t('resumeSelected')
      : t('resume', { count: resumableCount });
  const rerunLabel =
    rerunableCount === null
      ? t('sendToNeoSelected')
      : t('sendToNeo', { count: rerunableCount });
  const abortLabel =
    abortableCount === null
      ? t('abortSelected')
      : t('abort', { count: abortableCount });

  return (
    <ListPageSelectionBar
      selectedCount={selectedCount}
      clearSelectionLabel={t('clearSelection')}
      onClearSelection={onClearSelection}
    >
      <ListPageBulkActionButton
        label={resumeLabel}
        tooltip={mutationsDisabledTooltip ?? resumeLabel}
        icon={isBulkResuming ? <SpinningIcon /> : <PlayIcon size={12} />}
        isDisabled={isLocked || resumableCount === 0}
        onClick={onResume}
      />
      <ListPageBulkActionButton
        label={rerunLabel}
        tooltip={mutationsDisabledTooltip ?? rerunLabel}
        icon={
          isBulkRerunning ? <SpinningIcon /> : <ExecutionNeoIcon size={12} />
        }
        isDisabled={isLocked || rerunableCount === 0}
        onClick={onRerun}
      />
      <ListPageBulkActionButton
        label={abortLabel}
        tooltip={mutationsDisabledTooltip ?? abortLabel}
        icon={isBulkAborting ? <SpinningIcon /> : <StopIcon size={12} />}
        isDisabled={isLocked || abortableCount === 0}
        onClick={onAbort}
      />
      <ListPageBulkActionButton
        label={tCommon('delete')}
        tooltip={mutationsDisabledTooltip ?? tCommon('delete')}
        icon={isBulkDeleting ? <SpinningIcon /> : <Delete02Icon size={12} />}
        isDisabled={isLocked}
        onClick={onDelete}
      />
    </ListPageSelectionBar>
  );
}
